
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { RefreshCw } from "lucide-react";
import ConversationDataCard from "./ConversationDataCard";
import ConversationAudioPlayer from "./ConversationAudioPlayer";
import ConversationTranscriptView from "./ConversationTranscriptView";

interface ConversationDetailSidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedConversation: string | null;
  conversationDetails: any;
  isLoadingDetails: boolean;
  audioData: any;
  isLoadingAudio: boolean;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  handleFetchAudio: (conversationId: string) => Promise<void>;
  truncateId: (id: string | undefined) => string;
}

export const ConversationDetailSidebar = ({
  isOpen,
  onOpenChange,
  selectedConversation,
  conversationDetails,
  isLoadingDetails,
  audioData,
  isLoadingAudio,
  currentTab,
  setCurrentTab,
  handleFetchAudio,
  truncateId
}: ConversationDetailSidebarProps) => {
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-full md:max-w-[80%] lg:max-w-[50%] overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex justify-between items-center">
            Conversation Details
            {selectedConversation && (
              <Badge variant="outline" className="font-mono text-xs">
                {truncateId(selectedConversation)}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>
        
        {isLoadingDetails ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mb-4 text-gray-400" />
            <p className="text-muted-foreground">Loading conversation details...</p>
          </div>
        ) : conversationDetails ? (
          <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
              <TabsTrigger value="audio" className="flex-1">Audio</TabsTrigger>
              <TabsTrigger value="transcript" className="flex-1">Transcript</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="pt-4">
              <ConversationDataCard conversationData={conversationDetails} />
            </TabsContent>
            
            <TabsContent value="audio" className="pt-4">
              <div className="rounded-md border p-4 bg-card">
                {audioData ? (
                  <ConversationAudioPlayer 
                    audioData={audioData}
                    conversationId={selectedConversation}
                    truncateId={truncateId}
                  />
                ) : (
                  <div className="flex flex-col items-center space-y-4 py-6">
                    <p className="text-muted-foreground mb-4">No audio available for this conversation</p>
                    <button 
                      className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 transition-colors"
                      onClick={() => selectedConversation && handleFetchAudio(selectedConversation)} 
                      disabled={isLoadingAudio || !selectedConversation}
                    >
                      {isLoadingAudio ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 inline-block animate-spin" />
                          Loading Audio...
                        </>
                      ) : (
                        <>
                          Fetch Audio
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="transcript" className="pt-4">
              <ConversationTranscriptView conversationDetails={conversationDetails} />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">No conversation details found</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
